import safeRegex from 'safe-regex2';

export interface RegexValidationResult {
  isValid: boolean;
  error?: string;
  maxLength?: number;
}

/**
 * Validates whether a regex pattern is syntactically valid and safe from ReDoS.
 */
export const validateSafeRegex = (pattern: string): RegexValidationResult => {
  if (!pattern || pattern.trim() === '') {
    return { isValid: false, error: 'emptyPattern' };
  }

  const MAX_PATTERN_LENGTH = 128;
  if (pattern.length > MAX_PATTERN_LENGTH) {
    return {
      isValid: false,
      error: 'patternTooLong',
      maxLength: MAX_PATTERN_LENGTH,
    };
  }

  try {
    new RegExp(pattern);
  } catch {
    return { isValid: false, error: 'invalidSyntax' };
  }

  if (!safeRegex(pattern)) {
    return { isValid: false, error: 'unsafePattern' };
  }

  return { isValid: true };
};

/**
 * Validates every regex fragment that will be combined into one expression.
 * Returning the first failure keeps the UI error handling consistent with
 * validateSafeRegex while ensuring no fragment bypasses client-side checks.
 */
export const validateSafeRegexFragments = (
  patterns: string[]
): RegexValidationResult => {
  for (const pattern of patterns) {
    const validation = validateSafeRegex(pattern);
    if (!validation.isValid) {
      return validation;
    }
  }

  return { isValid: true };
};

export const isSafeRegex = (pattern: string): boolean => {
  return validateSafeRegex(pattern).isValid;
};
